// The session list: every sitting the current user has recorded, newest first.
// Tapping one opens its detail (question → transcript pairs with playback).
//
// We query sessions by owner with equality only and sort newest-first in the
// client, so no composite index is needed (matching the rest of the app).

import { collection, getDocs, query, where } from "firebase/firestore";
import { db, ensureSignedIn } from "./firebase";
import { formatDate, formatTime, startedMillis } from "./session-data";

const listEl = document.getElementById("sessionsList") as HTMLUListElement;
const emptyEl = document.getElementById("sessionsEmpty") as HTMLElement;
const loadingEl = document.getElementById("sessionsLoading") as HTMLElement;

export async function renderSessionList(): Promise<void> {
  listEl.replaceChildren();
  emptyEl.hidden = true;
  loadingEl.hidden = false;

  let docs;
  try {
    const uid = await ensureSignedIn();
    const snap = await getDocs(
      query(collection(db, "sessions"), where("createdBy", "==", uid)),
    );
    docs = snap.docs;
  } catch (err) {
    loadingEl.hidden = false;
    loadingEl.textContent = `Couldn't load your sessions: ${(err as Error).message}`;
    return;
  }

  loadingEl.hidden = true;

  const sessions = docs
    .map((d) => ({ id: d.id, data: d.data() }))
    .sort((a, b) => startedMillis(b.data) - startedMillis(a.data));

  if (sessions.length === 0) {
    emptyEl.hidden = false;
    return;
  }

  for (const { id, data } of sessions) {
    listEl.append(createSessionRow(id, data));
  }
}

function createSessionRow(id: string, data: Record<string, unknown>): HTMLLIElement {
  const li = document.createElement("li");
  li.className = "session-row";

  // The whole row is a link into the session's detail view.
  const link = document.createElement("a");
  link.className = "session-row-link";
  link.href = `#/session/${id}`;

  const title = document.createElement("p");
  title.className = "session-row-title";
  const named = (data.title as string | undefined)?.trim();
  // Untitled sittings read as their date, so the list is never a wall of
  // identical placeholders.
  title.textContent = named || formatDate(data.startedAt);

  const meta = document.createElement("p");
  meta.className = "session-row-meta";
  // When a title is set, the date moves to the meta line so "when" is never lost.
  meta.textContent = named
    ? `${formatDate(data.startedAt)} · ${formatTime(data.startedAt)}`
    : formatTime(data.startedAt);

  link.append(title, meta);
  li.append(link);
  return li;
}
